import { useState } from "react";
import type { ThreadStepPlan } from "../../../../api/threads/threadsApi";
import type { GivenValues } from "../../stepPlans/PlannedInputsList";
import { withDraft, type CurrentStep } from "./stepFocus";
import { useDraftStepPlan } from "./useDraftStepPlan";

/** Values changed for one step's next attempt (S2: never carried to another step). */
interface RunValuesDraft {
  readonly forStep: string;
  readonly values: GivenValues;
}

const NO_DRAFT: RunValuesDraft = { forStep: "", values: {} };

/** The values typed into the current step and what they make of it (ADR-0032 D7). */
export interface RunDraft {
  /** The current step as the typed values leave it (`withDraft`). */
  readonly current: CurrentStep | null;
  /** The values typed for the current step; empty without any. */
  readonly values: GivenValues;
  /** Module B's plan for the step with them; `null` while none are typed or it hasn't answered. */
  readonly plan: ThreadStepPlan | null;
  /** Module B hasn't answered for the typed values yet: nothing runs with them until it does. */
  readonly pending: boolean;
  readonly change: (values: GivenValues) => void;
}

/**
 * The draft of the current step's values, held by the step list, so Run, "Run to here" and
 * "Run all" each send what is typed (PC-3), as an HTML form sends its values whichever submit
 * button is pressed.
 */
export function useRunDraft(threadId: string, focused: CurrentStep | null, lastEntryNumber: number): RunDraft {
  const [draft, setDraft] = useState<RunValuesDraft>(NO_DRAFT);
  const values = focused !== null && draft.forStep === focused.stepKey ? draft.values : NO_DRAFT.values;
  // Once the request went through, the step runs: its values were sent with it, so they go.
  if (focused?.state === "running" && draft.forStep === focused.stepKey) {
    setDraft(NO_DRAFT);
  }
  const plan = useDraftStepPlan(threadId, focused?.stepKey ?? null, values, lastEntryNumber);
  // Run follows module B's answer for the changed values, and waits while it comes.
  const { current, pending } = withDraft(focused, Object.keys(values).length > 0, plan === null ? null : plan.reasons.length);
  return {
    current,
    values,
    plan,
    pending,
    change: (changed) => {
      if (focused !== null) setDraft({ forStep: focused.stepKey, values: changed });
    },
  };
}
